import { memo, useState, useCallback, useEffect, useRef } from "react";
import { typography, transitions } from "../tokens";

function getInitials(name: string): string {
  return name
    .split(" ")
    .slice(0, 2)
    .map(w => w[0] ?? "")
    .join("")
    .toUpperCase();
}

export const DropdownSelector = memo(function DropdownSelector({
  items,
  activeId,
  onSelect,
  placeholder = "Select...",
}: {
  items: { id: string; name: string }[];
  activeId: string | null;
  onSelect: (id: string) => void;
  placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const handleEsc = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleEsc);
    return () => { document.removeEventListener("mousedown", handleClick); document.removeEventListener("keydown", handleEsc); };
  }, []);

  const activeItem = items.find(i => i.id === activeId);

  return (
    <div ref={ref} style={{ position: "relative", display: "inline-block" }}>
      <button
        onClick={() => setOpen(v => !v)}
        style={{
          display: "flex", alignItems: "center", gap: "8px",
          padding: "7px 12px 7px 7px",
          background: "#fff", border: "1.5px solid #e0e0e0", borderRadius: "10px",
          cursor: "pointer", transition: `all ${transitions.fast}`,
          maxWidth: "220px",
        }}
        onMouseEnter={e => ((e.currentTarget as HTMLButtonElement).style.borderColor = "#bbb")}
        onMouseLeave={e => ((e.currentTarget as HTMLButtonElement).style.borderColor = "#e0e0e0")}
      >
        {activeItem ? (
          <>
            <Avatar name={activeItem.name} />
            <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.semibold, color: "#111", flex: 1, textAlign: "left", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {activeItem.name}
            </span>
          </>
        ) : (
          <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#aaa", flex: 1, textAlign: "left" }}>
            {placeholder}
          </span>
        )}
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="2.5" strokeLinecap="round"><polyline points={open ? "18 15 12 9 6 15" : "6 9 12 15 18 9"} /></svg>
      </button>

      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 6px)", left: 0, minWidth: "50%", maxWidth: "28px",
          background: "#fff", border: "1px solid #e8e8e8", borderRadius: "12px",
          boxShadow: "0 8px 28px rgba(0,0,0,0.12)", zIndex: 50, padding: "5px",
          maxHeight: "220px", overflowY: "auto",
        }}>
          {items.map(item => {
            const isActive = item.id === activeId;
            return (
              <button
                key={item.id}
                onClick={() => { onSelect(item.id); setOpen(false); }}
                style={{
                  width: "100%", display: "flex", alignItems: "center", gap: "8px",
                  padding: "7px 10px", background: isActive ? "#f5f5f5" : "transparent",
                  border: "none", borderRadius: "8px", cursor: "pointer",
                  transition: `background ${transitions.fast}`, textAlign: "left",
                }}
                onMouseEnter={e => { if (!isActive) (e.currentTarget as HTMLButtonElement).style.background = "#f9f9f9"; }}
                onMouseLeave={e => { if (!isActive) (e.currentTarget as HTMLButtonElement).style.background = "transparent"; }}
              >
                <Avatar name={item.name} small />
                <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: isActive ? typography.weight.semibold : typography.weight.medium, color: "#111", flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {item.name}
                </span>
                {isActive && (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
});

const Avatar = memo(function Avatar({ name, small = false }: { name: string; small?: boolean }) {
  const initials = getInitials(name);
  const size = small ? "28px" : "32px";
  const fontSize = small ? "10px" : "11px";

  return (
    <div style={{
      width: size, height: size, borderRadius: "8px",
      background: "#f0f0f0", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
      fontFamily: typography.fontFamily, fontSize, fontWeight: typography.weight.bold, color: "#555", letterSpacing: "0.02em",
    }}>
      {initials}
    </div>
  );
});
